/* tslint:disable:variable-name */
import { Inject, Injectable } from '@angular/core';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { map, catchError } from 'rxjs/operators';
import {
    IReachAndFrequencyReport, IMeta
} from './interfaces';
import { IParam } from '../common/interfaces';

import { Field, BaseEntity } from '../common/meta';
import { BaseSerializer } from '../common/base-serializer';
import { BasePersistentApiClient } from '../common/index';
import { Grouping, Filters, Measure, Interval, Period } from './types';
import {
    ISchema, instanceCodeClientT, paramsBuilderT, IParamsBuilder, IInstanceCodeClient,
    IRecursiveArray, IRequestOptions
} from '../common/interfaces';
import { BulkRemovable } from '../common/bulk-removable/bulk-removable';
import { Duplicable } from '../common/duplicable/duplicable';
import { AuthHttp } from '../authentication/authentication.service';
import { environment } from '../../src/environments/environment';
import { ReportScheduleType, ReportSendAtType } from '../general-report/types';
import { IReportFormat } from '../../src/app/modules/report/common/interfaces';

@Injectable()
@BulkRemovable()
@Duplicable()
export class ReachAndFrequencyReportClient
    extends BasePersistentApiClient
        <IReachAndFrequencyReport, never, never> {
    protected static classConfig: ISchema = {
        apiResourceName: 'slicer_reports'
    };

    duplicate: (id: string) => Observable<IReachAndFrequencyReport>;

    constructor(
        http: AuthHttp,
        @Inject(instanceCodeClientT) instanceCodeClient: IInstanceCodeClient,
        @Inject(paramsBuilderT) paramsBuilder: IParamsBuilder
    ) {
        super(http, instanceCodeClient, paramsBuilder);
    }

    protected implementationClass() {
        return ReachAndFrequencyReport;
    }

    get(id: string, include?: IRecursiveArray<string>,
        options: IRequestOptions = {}): Observable<IReachAndFrequencyReport> {
        return super.get(id, include, options);
    }

    generate(entity: IReachAndFrequencyReport, params: IParam): Observable<IReachAndFrequencyReport> {
        this.recreateParams();

        this.setImpersonationParams();

        let wrappedEntity = this.wrapAndFilterEntity(entity);
        let data = this.serializer.serialize(wrappedEntity);
        return this.http.post(
            `${ this.apiBase() }/generate`, data,
            { params: this.prepareParams(params, {page: 'report_page'}) }
            )
            .pipe(
                map((response: HttpResponse<any>) => {
                    return this.serializer.deserialize(response.body);
                }),
                catchError(this.handleRequestError)
            );
    }

    saveWithFile(entity: IReachAndFrequencyReport, format: IReportFormat): Observable<IReachAndFrequencyReport> {
        this.customParams = new HttpParams();
        this.customParams = this.customParams.append('generate_file', format);
        return super.save(entity);
    }

    enqueueEmail(reportId: string): Observable<IReachAndFrequencyReport> {
        this.recreateParams();
        let serializer = new BaseSerializer<IReachAndFrequencyReport>(ReachAndFrequencyReport);
        this.setImpersonationParams();
        return this.http.post(`${this.apiBase()}/${reportId}/email`, '', { params: this.prepareParams() })
            .pipe(
                map(
                (response: HttpResponse<any>) => {
                    return serializer.deserialize(response.body);
                }
                ),
                catchError(this.handleRequestError)
            );
    }

    protected apiBase(): string {
        return `${environment.api.internal}/${this.instanceCode}/${this.classConfig().apiResourceName}`;
    }

    private wrapAndFilterEntity(entity): ReachAndFrequencyReport {
        return new (this.implementationClass())(entity);
    }
}

export class ReachAndFrequencyReport extends BaseEntity<IReachAndFrequencyReport> implements IReachAndFrequencyReport {

    public static readonly type: string = 'slicer_report';

    @Field()
    id: string;

    @Field()
    name: string;

    @Field()
    period: Period;

    @Field()
    date_limit: 'none' | 'range' | 'period';

    @Field()
    interval: Interval;

    @Field()
    start_on: string;

    @Field()
    end_on: string;

    @Field()
    measures: Measure[];

    @Field()
    groupings: Grouping[];

    @Field()
    filters: Filters;

    @Field()
    recipients: string;

    @Field()
    body: string;

    @Field()
    schedule: ReportScheduleType;

    @Field()
    schedule_format: string;

    @Field()
    schedule_start_on: string;

    @Field()
    schedule_end_on: string;

    @Field()
    send_at: ReportSendAtType;

    @Field()
    sort?: string[] | string;

    @Field()
    on_business_days: boolean;

    @Field()
    date: string;

    @Field()
    data_rows?: any[];

    @Field()
    totals?: any[];

    @Field()
    headers?: string[];

    @Field()
    meta?: IMeta;
}
